import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const ORDER_STORAGE_KEY = "hushcider:last-order";

const COUNTRY_OPTIONS = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "JP", label: "Japan" },
  { code: "HK", label: "Hong Kong SAR" },
  { code: "SG", label: "Singapore" },
  { code: "MY", label: "Malaysia" },
  { code: "TH", label: "Thailand" },
  { code: "KR", label: "South Korea" },
  { code: "NZ", label: "New Zealand" },
  { code: "NL", label: "Netherlands" },
  { code: "IT", label: "Italy" },
  { code: "ES", label: "Spain" },
  { code: "SE", label: "Sweden" },
];

const initialValues = {
  contact: "",
  country: "US",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  address2: "",
  city: "",
  region: "",
  postalCode: "",
  acceptTerms: false,
};

const isValidContact = (value) => {
  const contact = value.trim();
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact);
  return isEmail;
};

const createOrderNumber = () => {
  return `${Date.now()}${Math.floor(100000 + Math.random() * 900000)}`;
};

const CheckOut = () => {
  const location = useLocation();
  const { cartItems } = useContext(CartContext);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const lines = useMemo(
    () =>
      cartItems.reduce((items, item) => {
        const product = productData.find(
          (candidate) => Number(candidate.id) === Number(item.id)
        );
        if (!product) return items;

        const unitPrice = Number(product.price);
        const quantity = Math.max(1, Number(item.quantity) || 1);
        return [
          ...items,
          {
            ...item,
            quantity,
            product,
            unitPrice,
            lineTotal: unitPrice * quantity,
          },
        ];
      }, []),
    [cartItems]
  );

  const subtotal = lines.reduce((total, line) => total + line.lineTotal, 0);
  const itemCount = lines.reduce((total, line) => total + line.quantity, 0);
  const promotion = location.state?.checkoutPromotion;
  const promotionRate =
    promotion?.code === "HUSH10"
      ? Math.min(Math.max(Number(promotion.rate) || 0, 0), 0.1)
      : 0;
  const discount = subtotal * promotionRate;
  const shipping = siteConfig.commerce.standardShipping;
  const total = Math.max(0, subtotal - discount + shipping);

  const changeValue = (event) => {
    const { name, type, checked, value } = event.target;
    const nextValue = type === "checkbox" ? checked : value;

    setValues((current) => ({ ...current, [name]: nextValue }));
    if (errors[name] || errors.form) {
      setErrors((current) => ({ ...current, [name]: "", form: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!isValidContact(values.contact)) {
      nextErrors.contact = "Enter a valid email address.";
    }
    if (!values.country) nextErrors.country = "Select a country or region.";
    if (!values.firstName.trim()) nextErrors.firstName = "Enter a first name.";
    if (!values.lastName.trim()) nextErrors.lastName = "Enter a last name.";
    if (!values.address.trim()) nextErrors.address = "Enter a street address.";
    if (!values.city.trim()) nextErrors.city = "Enter a city.";
    if (!values.postalCode.trim()) {
      nextErrors.postalCode = "Enter a ZIP or postal code.";
    }
    if (!values.acceptTerms) {
      nextErrors.acceptTerms = "Confirm the terms before placing the order.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || lines.length === 0) return;

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors({
        ...nextErrors,
        form: "Review the highlighted fields before continuing.",
      });

      const firstError = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => {
        document.getElementById(`checkout-${firstError}`)?.focus();
      });
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const countryName =
      COUNTRY_OPTIONS.find((country) => country.code === values.country)
        ?.label || values.country;
    const shippingLabel = "Tracked shipping";
    const orderNumber = createOrderNumber();
    const orderSummary = {
      orderNumber,
      placedAt: new Date().toISOString(),
      customerName: `${values.firstName.trim()} ${values.lastName.trim()}`,
      contact: values.contact.trim(),
      destination: [values.city.trim(), values.region.trim(), countryName]
        .filter(Boolean)
        .join(", "),
      shippingMethod: shippingLabel,
      deliveryEstimate: "3–10 business days after dispatch",
      paymentMethod: "External secure payment",
      itemCount,
      subtotal,
      discount,
      discountCode: promotionRate ? promotion.code : "",
      shipping,
      total,
      items: lines.map((line) => ({
        id: line.product.id,
        name: line.product.name,
        image: line.product.images?.[0] || line.product.image,
        quantity: line.quantity,
        size: line.size || "",
        color: line.color || "",
        lineTotal: line.lineTotal,
      })),
    };

    try {
      window.sessionStorage.setItem(
        ORDER_STORAGE_KEY,
        JSON.stringify(orderSummary)
      );
    } catch {
      // Navigation state keeps confirmation available if storage is disabled.
    }

    const endpoint = new URL("/create_payment.php", window.location.origin);
    endpoint.search = new URLSearchParams({
      orderNo: orderNumber,
      amount: total.toFixed(2),
      currency: siteConfig.commerce.currency,
      email: values.contact.trim(),
      success_url: `${window.location.origin}/payment?state=success`,
      cancel_url: `${window.location.origin}/payment?state=cancel`,
      title: lines[0].product.name,
    }).toString();
    window.location.assign(endpoint.toString());
  };

  const fieldError = (name) =>
    errors[name] ? (
      <p className="checkout-field__error" id={`checkout-${name}-error`}>
        {errors[name]}
      </p>
    ) : null;

  const fieldProps = (name) => ({
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `checkout-${name}-error` : undefined,
  });

  if (lines.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty">
        <section className="checkout-empty" aria-labelledby="checkout-empty-title">
          <p className="checkout-eyebrow">{siteConfig.wordmark} / CHECKOUT</p>
          <h1 id="checkout-empty-title">NOTHING TO CHECK OUT.</h1>
          <p>Add gear to your bag before starting the secure checkout.</p>
          <Link className="checkout-primary-link" to="/shop">
            SHOP ALL GEAR
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page" aria-busy={isSubmitting}>
      <header className="checkout-page__header">
        <div>
          <p className="checkout-eyebrow">{siteConfig.wordmark} / SECURE CHECKOUT</p>
          <h1>CHECKOUT</h1>
        </div>
        <nav className="checkout-breadcrumbs" aria-label="Checkout progress">
          <Link to="/cartpage">CART</Link>
          <span aria-hidden="true">/</span>
          <strong>INFORMATION</strong>
          <span aria-hidden="true">/</span>
          <span>CONFIRMATION</span>
        </nav>
      </header>

      <div className="checkout-layout">
        <form className="checkout-form" onSubmit={handleSubmit} noValidate>
          {errors.form && (
            <div className="checkout-form__notice" role="alert">
              {errors.form}
            </div>
          )}

          <section className="checkout-section" aria-labelledby="checkout-contact-title">
            <div className="checkout-section__heading">
              <span>01</span>
              <div>
                <p>ACCOUNT / GUEST</p>
                <h2 id="checkout-contact-title">CONTACT</h2>
              </div>
            </div>
            <div className="checkout-section__body">
              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-contact">EMAIL</label>
                <input
                  id="checkout-contact"
                  name="contact"
                  type="text"
                  value={values.contact}
                  onChange={changeValue}
                  placeholder="you@example.com"
                  autoComplete="email"
                  {...fieldProps("contact")}
                />
                {fieldError("contact")}
              </div>
              <p className="checkout-section__hint">
                Your receipt and shipment updates will be sent here.
              </p>
            </div>
          </section>

          <section className="checkout-section" aria-labelledby="checkout-delivery-title">
            <div className="checkout-section__heading">
              <span>02</span>
              <div>
                <p>SHIP TO / ADDRESS</p>
                <h2 id="checkout-delivery-title">DELIVERY</h2>
              </div>
            </div>
            <div className="checkout-section__body checkout-fields">
              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-country">COUNTRY / REGION</label>
                <select
                  id="checkout-country"
                  name="country"
                  value={values.country}
                  onChange={changeValue}
                  autoComplete="country"
                  {...fieldProps("country")}
                >
                  {COUNTRY_OPTIONS.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>
                {fieldError("country")}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-firstName">FIRST NAME</label>
                <input
                  id="checkout-firstName"
                  name="firstName"
                  value={values.firstName}
                  onChange={changeValue}
                  autoComplete="given-name"
                  {...fieldProps("firstName")}
                />
                {fieldError("firstName")}
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-lastName">LAST NAME</label>
                <input
                  id="checkout-lastName"
                  name="lastName"
                  value={values.lastName}
                  onChange={changeValue}
                  autoComplete="family-name"
                  {...fieldProps("lastName")}
                />
                {fieldError("lastName")}
              </div>
              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-company">COMPANY (OPTIONAL)</label>
                <input
                  id="checkout-company"
                  name="company"
                  value={values.company}
                  onChange={changeValue}
                  autoComplete="organization"
                />
              </div>
              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-address">STREET ADDRESS</label>
                <input
                  id="checkout-address"
                  name="address"
                  value={values.address}
                  onChange={changeValue}
                  autoComplete="address-line1"
                  {...fieldProps("address")}
                />
                {fieldError("address")}
              </div>
              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-address2">APT, SUITE, UNIT (OPTIONAL)</label>
                <input
                  id="checkout-address2"
                  name="address2"
                  value={values.address2}
                  onChange={changeValue}
                  autoComplete="address-line2"
                />
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-city">CITY</label>
                <input
                  id="checkout-city"
                  name="city"
                  value={values.city}
                  onChange={changeValue}
                  autoComplete="address-level2"
                  {...fieldProps("city")}
                />
                {fieldError("city")}
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-region">STATE / PROVINCE</label>
                <input
                  id="checkout-region"
                  name="region"
                  value={values.region}
                  onChange={changeValue}
                  autoComplete="address-level1"
                />
              </div>
              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-postalCode">ZIP / POSTAL CODE</label>
                <input
                  id="checkout-postalCode"
                  name="postalCode"
                  value={values.postalCode}
                  onChange={changeValue}
                  autoComplete="postal-code"
                  {...fieldProps("postalCode")}
                />
                {fieldError("postalCode")}
              </div>

              <div className="checkout-shipping checkout-field--full">
                <strong>TRACKED SHIPPING</strong>
                <span>3–10 business days after dispatch</span>
                <b>{formatPrice(shipping)}</b>
              </div>
            </div>
          </section>

          <section className="checkout-section" aria-labelledby="checkout-review-title">
            <div className="checkout-section__heading">
              <span>03</span>
              <div>
                <p>FINAL CHECK / SUBMIT</p>
                <h2 id="checkout-review-title">REVIEW</h2>
              </div>
            </div>
            <div className="checkout-section__body">
              <div className="checkout-review">
                <div>
                  <span>CONTACT</span>
                  <strong>{values.contact || "Not entered"}</strong>
                </div>
                <div>
                  <span>DELIVERY</span>
                  <strong>
                    {values.city && values.postalCode
                      ? `${values.city}, ${values.postalCode}`
                      : "Address incomplete"}
                  </strong>
                </div>
                <div>
                  <span>PAYMENT</span>
                  <strong>SECURE PAYMENT PAGE</strong>
                </div>
              </div>

              <label className="checkout-consent" htmlFor="checkout-acceptTerms">
                <input
                  id="checkout-acceptTerms"
                  name="acceptTerms"
                  type="checkbox"
                  checked={values.acceptTerms}
                  onChange={changeValue}
                  {...fieldProps("acceptTerms")}
                />
                <span>
                  I confirm this order and agree to the {" "}
                  <Link to="/terms" target="_blank" rel="noreferrer">Terms</Link> and {" "}
                  <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Policy</Link>.
                </span>
              </label>
              {fieldError("acceptTerms")}

              <button
                className="checkout-submit"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="checkout-submit__spinner" aria-hidden="true" />
                    OPENING SECURE PAYMENT
                  </>
                ) : (
                  <>
                    PAY NOW · {formatPrice(total)}
                    <span aria-hidden="true">→</span>
                  </>
                )}
              </button>
            </div>
          </section>
        </form>

        <aside className="checkout-summary" aria-labelledby="checkout-summary-title">
          <div className="checkout-summary__sticky">
            <div className="checkout-summary__heading">
              <div>
                <p className="checkout-eyebrow">ORDER / {String(itemCount).padStart(2, "0")}</p>
                <h2 id="checkout-summary-title">YOUR GEAR</h2>
              </div>
              <Link to="/cartpage">EDIT</Link>
            </div>

            <div className="checkout-summary__items">
              {lines.map((line) => (
                <article
                  className="checkout-summary-item"
                  key={`${line.id}-${line.size}-${line.color}`}
                >
                  <div className="checkout-summary-item__media">
                    <img
                      src={line.product.images?.[0] || line.product.image}
                      alt={line.product.name}
                    />
                    <span>{line.quantity}</span>
                  </div>
                  <div className="checkout-summary-item__details">
                    <h3>{line.product.name}</h3>
                    <p>{[line.size, line.color].filter(Boolean).join(" / ")}</p>
                  </div>
                  <strong>{formatPrice(line.lineTotal)}</strong>
                </article>
              ))}
            </div>

            <div className="checkout-summary__rows">
              <div>
                <span>SUBTOTAL</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="is-discount">
                  <span>PROMOTION ({promotion.code})</span>
                  <span>−{formatPrice(discount)}</span>
                </div>
              )}
              <div>
                <span>SHIPPING</span>
                <span>{shipping === 0 ? "FREE" : formatPrice(shipping)}</span>
              </div>
              <div>
                <span>EST. TAX</span>
                <span>CALCULATED</span>
              </div>
            </div>
            <div className="checkout-summary__total">
              <span>TOTAL · USD</span>
              <strong>{formatPrice(total)}</strong>
            </div>
            <p className="checkout-summary__secure">
              PAYMENT DETAILS ARE COLLECTED ON THE SECURE PAYMENT PAGE
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
